/**
 * @file 知识库列表 / 文档列表 — 接口响应归一化（Layer 2b 纯函数）
 */
import { compact, groupBy, keyBy, sortBy, uniq } from 'lodash-es';
import http from '@/utils/http';
import type {
  IKnowledgeBaseItem,
  IKnowledgeDocItem,
} from '../hooks/useKnowledgeBaseApi';
import type { ISelectedKnowledgeTagData } from '../components/SelectedKnowledgeTag';
import { getKnowledgeTagKey } from '../components/SelectedKnowledgeTag';

/** 知识文档列表查询参数（写死） */
export const KB_DOCS_QUERY =
  'page=1&page_size=999&sort_by=updated_at&sort_order=desc';

/**
 * 按优先级解析空间 ID（tenantId），返回第一个非空值。
 *
 * 知识库列表 / 文档列表 / 历史选区可能各自携带 tenantId，兜底优先级：
 * 文档（doc）→ 知识库（kb）→ 历史选区（prev），避免多处重复 `a || b || c` 兜底。
 *
 * @param candidates - 按优先级从高到低排列的候选值
 * @returns 第一个非空字符串，全为空返回 undefined
 */
export const resolveTenantId = (
  ...candidates: Array<string | null | undefined>
): string | undefined => candidates.find((v) => v) as string | undefined;

/**
 * 将知识库列表接口的原始响应映射为 IKnowledgeBaseItem[]
 * 接口返回 { data: [...], success: true }
 */
export const normalizeKbList = (data: unknown): IKnowledgeBaseItem[] => {
  const raw = (data as { data?: unknown; })?.data ?? [];
  if (!Array.isArray(raw)) return [];
  const items: IKnowledgeBaseItem[] = raw.map((item: any) => ({
    kbId: item.id,
    kbName: item.name,
    type: item.type === 'faq' ? 'faq' as const : 'document' as const,
    tenantId: item.tenant_id === null || item.tenant_id === undefined
      ? undefined
      : String(item.tenant_id),
    docCount: item.knowledge_count ?? 0,
    updatedAt: item.updated_at,
  }));
  return sortBy(items, 'kbName');
};

/** 知识库是否含至少一篇文档（优先以已合并的 docs 为准） */
export const hasKnowledgeBaseDocs = (kb: IKnowledgeBaseItem): boolean => {
  if (kb.docs) {
    return kb.docs.length > 0;
  }
  return (kb.docCount ?? 0) > 0;
};

/** 过滤零篇知识库（未加载 docs 的库看 docCount，不把 undefined 当空列表） */
export const filterKnowledgeBasesWithDocs = (
  kbList: IKnowledgeBaseItem[],
): IKnowledgeBaseItem[] => kbList.filter(hasKnowledgeBaseDocs);

/** 单库文档 cacheKey 前缀，与列表 KnowledgeBaseList 对齐 */
export const KB_DOCS_CACHE_KEY_PREFIX = 'KnowledgeBaseDocs:';

/** 单库文档 ahooks 缓存时长，与列表 30min 一致 */
export const KB_DOCS_CACHE_TTL_MS = 1800000;

/** 文档 loading 延迟展示（与 useRequestController loadingDelay 一致，<300ms 不展示） */
export const KB_DOCS_LOADING_DELAY_MS = 300;

/** 知识库面板搜索防抖（列表 keyword + 高亮库切换拉文档） */
export const KB_PANEL_SEARCH_DEBOUNCE_MS = 500;

/**
 * 单库文档 useRequest cacheKey：KnowledgeBaseDocs:{kbId}
 * @param kbId - 知识库 id
 * @returns ahooks cacheKey
 */
export const getKbDocsCacheKey = (kbId: string): string =>
  `${KB_DOCS_CACHE_KEY_PREFIX}${kbId}`;

/**
 * 从 pending 队列中选出下一个待拉文档的 kbId；优先当前面板高亮库
 * @param pendingKbIds - 待拉队列
 * @param docsByKbId - 已写入的文档缓存
 * @param kbMap - kbId → 列表项
 * @param priorityKbId - 面板当前高亮 kbId（优先）
 * @returns 下一个 docsKbId，无则空串
 */
export const pickNextInteractiveDocsKbId = (
  pendingKbIds: string[],
  docsByKbId: Record<string, IKnowledgeDocItem[]>,
  kbMap: Record<string, IKnowledgeBaseItem>,
  priorityKbId = '',
): string => {
  const isFetchable = (id: string): boolean => {
    if (docsByKbId[id] !== undefined) {
      return false;
    }
    const kb = kbMap[id];
    if (!kb) {
      return false;
    }
    return (kb.docCount ?? 0) > 0 || Boolean(kb.docs?.length);
  };
  const priority = String(priorityKbId || '');
  if (priority && pendingKbIds.includes(priority) && isFetchable(priority)) {
    return priority;
  }
  return pendingKbIds.find(isFetchable) || '';
};

/**
 * 知识库列表请求 URL。有 keyword 时带 scope=user 过滤，结构与全量 list 相同。
 * @param keyword - 搜索词；空则全量 list
 * @returns 列表接口路径
 */
export const buildKnowledgeBaseListUrl = (keyword?: string): string => {
  const trimmed = (keyword || '').trim();
  if (!trimmed) {
    return '/api/kb/v1/knowledge-bases';
  }
  const query = new URLSearchParams();
  query.set('scope', 'user');
  query.set('keyword', trimmed);
  return `/api/kb/v1/knowledge-bases?${query.toString()}`;
};

/**
 * 列表 cacheKey：全量与带 keyword 的结果分开，避免搜索盖掉全量缓存
 * @param keyword - 搜索词
 * @returns useRequest cacheKey
 */
export const getKnowledgeBaseListCacheKey = (keyword?: string): string => {
  const trimmed = (keyword || '').trim();
  if (!trimmed) {
    return 'KnowledgeBaseList';
  }
  return `KnowledgeBaseList:${trimmed}`;
};

/**
 * 把已加载文档合并进列表。未出现在 map 里的库保持 docs 为 undefined。
 * @param kbList - 知识库列表
 * @param docsByKbId - 已加载文档，key 为 kbId
 * @returns 合并后的列表
 */
export const mergeKbListWithDocs = (
  kbList: IKnowledgeBaseItem[],
  docsByKbId: Record<string, IKnowledgeDocItem[]>,
): IKnowledgeBaseItem[] =>
  kbList.map((kb) => {
    const docs = docsByKbId[String(kb.kbId)];
    if (docs === undefined) {
      return kb;
    }
    return {
      ...kb,
      docs,
      docCount: docs.length || kb.docCount,
    };
  });

/** 构建 X-Tenant-ID 请求头（未传 tenantId 时不附 header） */
const buildKbDocsTenantHeaders = (
  tenantId?: string,
): Record<string, string> | undefined => {
  if (!tenantId) {
    return undefined;
  }
  return { 'X-Tenant-ID': tenantId };
};

/**
 * 拉取单库文档列表（供 useKbDocs 内 useRequest，cacheKey 在 Hook 层）。
 * @param kbId - 知识库 ID
 * @param tenantId - 可选团队 ID
 * @returns 归一化后的文档列表
 */
export const fetchKnowledgeBaseDocs = async (
  kbId: string,
  tenantId?: string,
): Promise<IKnowledgeDocItem[]> => {
  const url = `/api/kb/v1/knowledge-bases/${kbId}/knowledge?${KB_DOCS_QUERY}`;
  const result = await http(url, {
    method: 'GET',
    rawData: true,
    ignoreCode: true,
    headers: buildKbDocsTenantHeaders(tenantId),
  });
  return normalizeKnowledgeDocs(result, kbId);
};

/**
 * 知识库文档展示名：title > display_file_name > source。
 * 供按钮搜索双栏、@ 下拉共用（都读归一化后的 docName）。
 * @param item - 文档列表接口单条原始数据
 * @returns 第一个非空字符串；全空则返回空串
 */
export const resolveKbDocDisplayName = (
  item: Record<string, any>,
): string => {
  const {
    title,
    display_file_name: displayFileName,
    source,
  } = item;
  return title || displayFileName || source || '';
};

/**
 * 将知识文档列表接口的原始响应映射为 IKnowledgeDocItem[]
 * 接口返回 { data: [...], success: true }
 */
export const normalizeKnowledgeDocs = (
  data: unknown,
  kbId: string,
): IKnowledgeDocItem[] => {
  const raw = (data as { data?: unknown; })?.data ?? [];
  if (!Array.isArray(raw)) return [];
  return raw.map((item: Record<string, any>) => ({
    docId: item.id,
    docName: resolveKbDocDisplayName(item),
    kbId: item.knowledge_base_id || kbId,
    channel: item.channel,
    fileType: item.file_type,
    fileName: item.file_name,
    tenantId: item.tenant_id === null || item.tenant_id === undefined
      ? undefined
      : String(item.tenant_id),
  }));
};

/**
 * 将 query 中的 kbId 拆成 ID 列表。
 * 逗号分隔、trim、去空、去重且保序。
 * @param raw - URL query 原始值；非字符串视为无参数
 * @returns 知识库 ID 列表
 */
export const parseKbIds = (raw: unknown): string[] => {
  /** 非字符串无法按逗号拆分 */
  if (typeof raw !== 'string') {
    return [];
  }
  /** 先 trim 再丢掉空段，uniq 保序去重 */
  return uniq(compact(raw.split(',').map(id => id.trim())));
};

/**
 * 解析 URL 中的 agentId。
 * @param raw - URL query 原始值；非字符串视为无效
 * @returns 去掉首尾空白的 agentId；无效时为空串
 */
export const parseAgentId = (raw: unknown): string => {
  /** 非字符串无法作为 agentId */
  if (typeof raw !== 'string') {
    return '';
  }
  /** 只做 trim，不做合法性校验（由调用方对照授权列表） */
  return raw.trim();
};

/**
 * 把单个知识库下的文档展开为提交/展示用的引用项。
 * 缺 docId 或 docName 的文档跳过。
 * @param kb - 知识库（含 docs）
 * @returns 带 docId 的引用列表
 */
export const mapKbDocsToSelected = (
  kb: IKnowledgeBaseItem,
): ISelectedKnowledgeTagData[] =>
  compact(
    (kb.docs || []).map(doc => {
      /** 无文档主键或名称时无法生成 @ 标签 */
      if (!doc.docId || !doc.docName) {
        return undefined;
      }
      return {
        kbId: String(kb.kbId),
        kbName: kb.kbName,
        tenantId: resolveTenantId(doc.tenantId, kb.tenantId),
        docId: String(doc.docId),
        docName: doc.docName,
        fileName: doc.fileName,
        fileType: doc.fileType,
      };
    }),
  );

/**
 * 是否为整库引用：无文档 id、无文档名。
 * docName 兜底的历史残留文档不算整库。
 * @param item - 知识库 tag
 * @returns 整库时为 true
 */
export const isWholeKbTag = (
  item: Pick<ISelectedKnowledgeTagData, 'docId' | 'docName'>,
): boolean => !item.docId && !item.docName;

/**
 * 生成整库 @ 引用（提交 file_list 为空数组，输入框只带 @库名）。
 * @param kb - 知识库列表项
 * @returns 不含 docId/docName 的 tag
 */
export const buildWholeKbSelectedTag = (
  kb: IKnowledgeBaseItem,
): ISelectedKnowledgeTagData => ({
  kbId: String(kb.kbId),
  kbName: kb.kbName,
  tenantId: kb.tenantId,
});

/**
 * 该库勾选是否已覆盖整库。
 * 以列表 docCount 为准，不能只用已加载页；已是整库 tag，
 * 或上次已是整库且当前已加载文档仍全勾，也视为整库（避免 page_size 截断把整库打回文档列表）。
 * @param group - 同一 kbId 的 tag
 * @param kb - 列表中的库；缺失时仅认整库 tag
 * @param previousItems - 提交前已有选区
 * @returns 应收成整库时为 true
 */
export const isWholeKbSelection = (
  group: ISelectedKnowledgeTagData[],
  kb: IKnowledgeBaseItem | undefined,
  previousItems: ISelectedKnowledgeTagData[] = [],
): boolean => {
  if (!group.length) {
    return false;
  }
  if (group.some(isWholeKbTag)) {
    return true;
  }
  if (!kb) {
    return false;
  }
  const selectedDocIds = uniq(
    compact(group.map(item => (item.docId ? String(item.docId) : undefined))),
  );
  const docCount = kb.docCount ?? 0;
  if (docCount > 0 && selectedDocIds.length >= docCount) {
    return true;
  }
  const prevWhole = previousItems.some(
    item => String(item.kbId) === String(kb.kbId) && isWholeKbTag(item),
  );
  const loadedIds = compact((kb.docs || []).map(doc => String(doc.docId)));
  const allLoadedChecked =
    loadedIds.length > 0
    && loadedIds.every(id => selectedDocIds.includes(id));
  return prevWhole && allLoadedChecked;
};

/**
 * 勾齐的库收成一条整库 tag，其余保持文档 tag。按首次出现的 kbId 保序。
 * @param tags - 面板 / @ 选中的 tag
 * @param kbList - 知识库列表（用 docCount）
 * @param previousItems - 提交前已有选区
 * @returns 收口后的 tag 列表
 */
export const collapseSelectedKnowledgeToWholeKb = (
  tags: ISelectedKnowledgeTagData[],
  kbList: IKnowledgeBaseItem[],
  previousItems: ISelectedKnowledgeTagData[] = [],
): ISelectedKnowledgeTagData[] => {
  if (!tags.length) {
    return tags;
  }
  const kbMap = keyBy(kbList, kb => String(kb.kbId));
  const groups = groupBy(tags, item => String(item.kbId));
  const seen = new Set<string>();
  const result: ISelectedKnowledgeTagData[] = [];
  tags.forEach(item => {
    const kbId = String(item.kbId);
    if (seen.has(kbId)) {
      return;
    }
    seen.add(kbId);
    const group = groups[kbId];
    const kb = kbMap[kbId];
    if (!isWholeKbSelection(group, kb, previousItems)) {
      result.push(...group);
      return;
    }
    if (kb) {
      result.push(buildWholeKbSelectedTag(kb));
      return;
    }
    result.push({
      kbId: item.kbId,
      kbName: item.kbName,
      tenantId: item.tenantId,
    });
  });
  return result;
};

/**
 * 按 URL kbId 顺序命中知识库，预填整库 @库名（file_list: []）。
 * 未命中的 ID 静默跳过。不展开文档。
 * @param kbList - 知识库列表（不必已加载 docs）
 * @param kbIds - 去重保序后的 URL kbId
 * @returns 命中库的整库引用；无命中时为空数组
 */
export const collectSelectedKnowledgeByKbIds = (
  kbList: IKnowledgeBaseItem[] | undefined,
  kbIds: string[],
): ISelectedKnowledgeTagData[] => {
  const kbMap = keyBy(kbList || [], kb => String(kb.kbId));
  return compact(
    kbIds.map(kbId => {
      const kb = kbMap[String(kbId)];
      if (!kb) {
        return undefined;
      }
      return buildWholeKbSelectedTag(kb);
    }),
  );
};

/**
 * 从当前 search 中去掉 kbId、agentId，拼回 pathname + search + hash。
 * 供 Home 用 replaceState 清一次性外链参数，不产生新 history 记录。
 * @param search - `window.location.search`（含 `?`）
 * @param pathname - `window.location.pathname`
 * @param hash - `window.location.hash`
 * @returns 去掉两个参数后的完整 path
 */
export const stripKbPrefillSearchParams = (
  search: string,
  pathname: string,
  hash: string,
): string => {
  const params = new URLSearchParams(search);
  /** 一次性外链参数，读入 state 后即可删除 */
  params.delete('kbId');
  params.delete('agentId');
  const next = params.toString();
  /** 无剩余 query 时不要留下孤立 `?` */
  return `${pathname}${next ? `?${next}` : ''}${hash}`;
};

/** @ 下拉单个库分组 */
export interface IAtDropdownGroup {
  kb: IKnowledgeBaseItem;
  docs: IKnowledgeDocItem[];
}

/**
 * 派生 @ 下拉展示数据：合并已加载文档，只剔除整库已选 / 已选文档。
 * 列表本身由服务端 keyword 筛好，不再按文档名二次过滤。
 * @param atKbList - 当前 @ 库列表（全量或搜索结果）
 * @param docsMergedKbList - useInteractiveKbDocs 合并 docs 后的列表
 * @param wholeSelectedKbIds - 已整库选中的 kbId
 * @param selectedDocKeys - 已选文档 tag key（getKnowledgeTagKey）
 */
export const buildAtDropdownGroups = (
  atKbList: IKnowledgeBaseItem[],
  docsMergedKbList: IKnowledgeBaseItem[] | undefined,
  wholeSelectedKbIds: Set<string>,
  selectedDocKeys: Set<string>,
): IAtDropdownGroup[] => {
  const mergedKbMap = new Map(
    (docsMergedKbList || []).map((kb) => [String(kb.kbId), kb]),
  );

  return atKbList
    .filter((kb) => !wholeSelectedKbIds.has(String(kb.kbId)))
    .map((rawKb) => {
      const kb = mergedKbMap.get(String(rawKb.kbId)) ?? rawKb;
      const docs = (kb.docs || []).filter((doc) => {
        const docKey = getKnowledgeTagKey({
          kbId: kb.kbId,
          docId: doc.docId,
        });
        return !selectedDocKeys.has(docKey);
      });
      return { kb, docs };
    })
    .filter(({ kb, docs }) => {
      if (docs.length > 0) {
        return true;
      }
      return kb.docs === undefined && (kb.docCount ?? 0) > 0;
    });
};
